import { observer } from 'mobx-react';
import { Collapse, Divider } from 'antd';
import { t } from 'i18next';
import { useState } from 'react';
import { PlusOutlined } from '@ant-design/icons';

import { useStores } from '@/hooks/use-stores';
import { EPButton } from '@/components';

import { Entity } from '../../NodeWidget.types';
import { OnDeviceSensorTabContainer } from './OnDeviceSensorTab.styles';
import SensorForm from './SensorForm/SensorForm';

const SensorCollapse = observer(
  ({
    onDevice,
    entity,
    openedSensor,
    isAddingSensor,
    setHasUnsavedChanges,
    onChange,
  }: {
    onDevice: string;
    entity: Entity;
    openedSensor: string | undefined;
    isAddingSensor: boolean;
    setHasUnsavedChanges: (hasUnsavedChanges: boolean) => void;
    onChange: (key: string[]) => void;
  }) => {
    const { stationStore } = useStores();
    const sensor = stationStore.getDeviceById(onDevice);

    return (
      <Collapse
        key={onDevice}
        expandIconPosition="end"
        className="sensor-collapse"
        activeKey={openedSensor}
        onChange={onChange}
        items={[
          {
            key: onDevice,
            label: sensor?.name,
            children: (
              <SensorForm
                parentDevice={entity}
                onDevice={sensor}
                isAddingSensor={isAddingSensor}
                setHasUnsavedChanges={setHasUnsavedChanges}
                afterOpenChange={!!openedSensor}
              />
            ),
          },
        ]}
      />
    );
  },
);

const OnDeviceSensorTab = observer(
  ({
    entity,
    setHasUnsavedChanges,
  }: {
    entity: Entity;
    setHasUnsavedChanges: (hasUnsavedChanges: boolean) => void;
  }) => {
    const [openedSensor, setOpenedSensor] = useState<string | undefined>(undefined);
    const [isAddingSensor, setIsAddingSensor] = useState(entity.onDevices?.length === 0);

    const onChange = (key: string[]) => {
      if (isAddingSensor) setIsAddingSensor(false);
      setOpenedSensor(key?.[key?.length - 1]);
    };

    const onAddSensor = () => {
      setOpenedSensor('newSensor');
      setIsAddingSensor(true);
    };

    return (
      <OnDeviceSensorTabContainer>
        <div className="body">
          {entity.onDevices?.map(onDevice => (
            <SensorCollapse
              key={onDevice}
              onDevice={onDevice}
              entity={entity}
              openedSensor={openedSensor}
              isAddingSensor={isAddingSensor}
              setHasUnsavedChanges={setHasUnsavedChanges}
              onChange={onChange}
            />
          ))}

          {isAddingSensor && (
            <Collapse
              className="sensor-collapse"
              activeKey="newSensor"
              items={[
                {
                  key: 'newSensor',
                  label: t('addSensor'),
                  children: (
                    <SensorForm
                      parentDevice={entity}
                      onDevice={null}
                      isAddingSensor={isAddingSensor}
                      setIsAddingSensor={setIsAddingSensor}
                      setHasUnsavedChanges={setHasUnsavedChanges}
                      afterOpenChange={true}
                    />
                  ),
                  showArrow: false,
                },
              ]}
            />
          )}
        </div>

        {!isAddingSensor && (
          <>
            <Divider className="divider" />
            <div className="footer">
              <EPButton type="link" onClick={onAddSensor} icon={<PlusOutlined />}>
                {t('addOnDeviceSensor')}
              </EPButton>
            </div>
          </>
        )}
      </OnDeviceSensorTabContainer>
    );
  },
);

export default OnDeviceSensorTab;
